import React, { useState, useEffect } from 'react';

interface MeditationActivityProps {
  emotion: string;
  onComplete: () => void;
}

const MeditationActivity: React.FC<MeditationActivityProps> = ({ emotion, onComplete }) => {
  const [isActive, setIsActive] = useState(false);
  const [duration, setDuration] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const meditationDuration = 120; // 2분

  useEffect(() => {
    let interval: number;
    if (isActive && !isPaused && duration < meditationDuration) {
      interval = window.setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    } else if (duration >= meditationDuration) {
      completeMeditation();
    }
    return () => clearInterval(interval);
  }, [isActive, isPaused, duration]);

  const startMeditation = () => {
    setIsActive(true);
    setDuration(0);
    setIsPaused(false);
  };

  const pauseMeditation = () => {
    setIsPaused(true);
  };

  const resumeMeditation = () => {
    setIsPaused(false);
  };

  const completeMeditation = () => {
    setIsActive(false);
    setIsCompleted(true);
    setTimeout(() => {
      onComplete();
    }, 2000);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isCompleted) {
    return (
      <div className="text-center">
        <div className="text-5xl mb-5">🧘</div>
        <h2 className="text-xl font-bold text-gray-900 mb-3">
          명상 완료!
        </h2>
        <p className="text-sm text-gray-600 mb-5">
          {emotion}을 명상으로 해소했습니다
        </p>
        <div className="bg-purple-50 rounded-2xl p-5 mb-5">
          <h3 className="text-base font-semibold mb-3">명상 요약</h3>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div>
              <div className="text-xl font-bold text-mint">{formatTime(meditationDuration)}</div>
              <div className="text-xs text-gray-600">명상 시간</div>
            </div>
            <div>
              <div className="text-xl font-bold text-mint">{emotion}</div>
              <div className="text-xs text-gray-600">감정</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center">
      <div className="text-5xl mb-5">🧘</div>
      <h2 className="text-xl font-bold text-gray-900 mb-3">
        명상
      </h2>
      <p className="text-sm text-gray-600 mb-5">
        잠시 눈을 감고 호흡에 집중하며 마음을 가라앉혀보세요
      </p>

      {!isActive ? (
        <div className="space-y-5">
          <div className="bg-purple-50 rounded-2xl p-5">
            <h3 className="text-base font-semibold mb-3">명상 가이드</h3>
            <div className="space-y-1.5 text-xs text-gray-600">
              <p>• 편안한 자세로 앉아주세요</p>
              <p>• 눈을 감고 호흡에 집중하세요</p>
              <p>• 생각이 떠오르면 다시 호흡으로 돌아오세요</p>
              <p>• 2분간 명상합니다</p>
            </div>
          </div>
          <button
            onClick={startMeditation}
            className="btn-primary px-6 py-2.5"
          >
            명상 시작
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="text-3xl font-bold text-mint mb-3">
            {formatTime(duration)}
          </div>
          <div className="text-base text-gray-700 mb-3">
            {isPaused ? '일시정지됨' : '명상 중...'}
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className="bg-mint h-1.5 rounded-full transition-all duration-1000"
              style={{ width: `${(duration / meditationDuration) * 100}%` }}
            ></div>
          </div>
          
          <div className="flex justify-center space-x-3">
            {isPaused ? (
              <button
                onClick={resumeMeditation}
                className="btn-primary px-5 py-2"
              >
                재개
              </button>
            ) : (
              <button
                onClick={pauseMeditation}
                className="btn-secondary px-5 py-2"
              >
                일시정지
              </button>
            )}
            <button
              onClick={completeMeditation}
              className="btn-primary px-5 py-2"
            >
              완료
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeditationActivity; 